import { useCallback } from "react";
import { useSelector } from "react-redux";

const DISPLAY_PERMISSION = "DISPLAY";

const usePermissionAccess = () => {
  const user = useSelector((state) => state.AuthReducer.user);
  const permissions = useSelector((state) => state.AuthReducer.permissions);
  const activeLocation = useSelector(
    (state) => state.SlickCareProfilesReducer.activeLocation,
  );

  const hasSubscriptionAccess = useCallback(
    (itemName) => {
      const currentLocation = user?.location?.find(
        (location) => location.id === activeLocation?.id,
      );
      const subscriptionData =
        currentLocation?.subscription_package_details?.item_name?.find(
          (item) => item.item_name === itemName,
        );

      return !!(
        subscriptionData?.id && subscriptionData?.subscription_status !== false
      );
    },
    [activeLocation?.id, user?.location],
  );

  // Some capabilities, such as buying a CareHearth enquiry, are paid for at
  // the point of use rather than through a location's module subscription.
  // Keep the RBAC check available independently so those screens can honour
  // the same employee permissions as their API endpoints.
  const hasPermissionAccess = useCallback(
    (itemName, requiredPermission = DISPLAY_PERMISSION) => {
      if (!itemName) return false;

      if (
        user?.is_master_admin === true ||
        user?.accounting_type === "administrator"
      ) {
        return true;
      }

      const userPermission = user?.permissions
        ?.map((permission) => ({
          ...permission.item_details,
          permission: permissions[permission.permission_id],
        }))
        ?.find((permission) => permission.item_name === itemName);

      return (
        userPermission &&
        permissions.indexOf(userPermission.permission) >=
          permissions.indexOf(requiredPermission)
      );
    },
    [permissions, user],
  );

  const hasItemAccess = useCallback(
    (itemName, requiredPermission = DISPLAY_PERMISSION) => {
      return (
        hasSubscriptionAccess(itemName) &&
        hasPermissionAccess(itemName, requiredPermission)
      );
    },
    [hasPermissionAccess, hasSubscriptionAccess],
  );

  const hasBooleanAccess = useCallback(
    (key) => {
      return !!(key && user?.[key] === true);
    },
    [user],
  );

  const hasMenuItemAccess = useCallback(
    (item, requiredPermission = DISPLAY_PERMISSION) => {
      if (!item) return false;

      const isAdmin =
        user?.is_master_admin === true ||
        user?.accounting_type === "administrator";

      if (item?.requiresBooleanCheck) {
        if (!hasBooleanAccess(item?.booleanCheckItem)) return false;
        return isAdmin || hasSubscriptionAccess(item?.item_name);
      }

      if (item?.requiresAnyBooleanAccess?.length) {
        if (isAdmin) return true;
        if (!hasSubscriptionAccess(item?.item_name)) return false;
        const hasBoolean = item.requiresAnyBooleanAccess.some(
          (key) => user?.[key] === true,
        );
        if (!hasBoolean) return false;
        // Opt-in: also require the item permission. Existing menus that only
        // set requiresAnyBooleanAccess keep the previous boolean-only check.
        if (item.requiresItemPermission) {
          return hasItemAccess(item.item_name);
        }
        return true;
      }

      if (
        item?.only_accessible_to_admin &&
        user?.accounting_type !== "administrator"
      ) {
        return false;
      }

      // Admins can discover every module from the sidebar even without a
      // subscription. Route-level itemValidator renders the subscribe landing.
      if (isAdmin) return Boolean(item.item_name);

      return hasItemAccess(item.item_name, requiredPermission);
    },
    [hasBooleanAccess, hasItemAccess, hasSubscriptionAccess, user],
  );

  const canAccessBilling =
    user?.is_master_admin === true ||
    user?.accounting_type === "administrator" ||
    (user?.accounting_type === "manager" && user?.can_manage_billing === true);

  return {
    hasBooleanAccess,
    hasItemAccess,
    hasPermissionAccess,
    hasMenuItemAccess,
    hasSubscriptionAccess,
    canAccessBilling,
  };
};

export default usePermissionAccess;
